

'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Copy, Info } from 'lucide-react';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Project } from '@/domains/projects/schemas';
import { Checkbox } from '../../../components/ui/checkbox';
import { Separator } from '../../../components/ui/separator';
import { DuplicateProjectFormSchema } from '@/domains/projects/schemas/project-details';
import { DUPLICATION_OPTION_DETAILS } from '@/domains/projects/constants';
import { rehydrateProjectState } from '@/domains/projects/services/project-service';
import { duplicateProject as duplicateProjectOnServer } from '@/domains/projects/services/create-project';
import { BaseDialog } from '@/components/shared/base-dialog';


type FormValues = z.infer<typeof DuplicateProjectFormSchema>;

interface DuplicateProjectDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  sourceProjectId: string;
}

export function DuplicateProjectDialog({ isOpen, onOpenChange, sourceProjectId }: DuplicateProjectDialogProps) {
  const router = useRouter();
  const { toast } = useToast();
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [sourceProject, setSourceProject] = useState<Project | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(DuplicateProjectFormSchema),
    defaultValues: {
      title: 'Project - Copy',
      options: {
        storySetup: true,
        plot: true,
        characters: true,
        worldElements: true,
        chapterOutlines: true,
        chapterContent: false,
        styleGuides: true,
      },
    },
  });

  const watchChapterOutlines = form.watch('options.chapterOutlines');

  React.useEffect(() => {
    if (!watchChapterOutlines) {
        form.setValue('options.chapterContent', false);
    }
  }, [watchChapterOutlines, form]);

  React.useEffect(() => {
    async function fetchSourceProject() {
      if (isOpen && sourceProjectId) {
        const project = await rehydrateProjectState(sourceProjectId);
        setSourceProject(project);
        if (project) {
          form.reset({
              title: `${project.title} - Copy`,
               options: {
                  storySetup: true,
                  plot: true,
                  characters: true,
                  worldElements: true,
                  chapterOutlines: true,
                  chapterContent: false,
                  styleGuides: true,
              },
          });
        }
      }
    }
    fetchSourceProject();
  }, [isOpen, sourceProjectId, form]);


  async function onSubmit(values: FormValues) {
    if (!sourceProject) {
        toast({ variant: 'destructive', title: 'Source project not found.' });
        return;
    }
    setIsDuplicating(true);
    try {
      const newProject = await duplicateProjectOnServer(sourceProject, values.title, values.options);
      toast({
        title: "Project Duplicated!",
        description: `"${newProject.title}" has been created.`,
      });
      onOpenChange(false);
      form.reset();
      // This will now be handled by the parent component re-fetching
      // router.push(`/projects/${newProject.id}`);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Duplication Failed',
        description: error.message || 'Could not duplicate the project. Please try again.',
      });
    } finally {
      setIsDuplicating(false);
    }
  }

  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="Duplicate Project"
      description={`Create a copy of "${sourceProject?.title ?? ''}". Select which parts to include.`}
      onSubmit={form.handleSubmit(onSubmit)}
      confirmLabel="Duplicate Project"
      confirmLoadingLabel="Duplicating..."
      confirmLoading={isDuplicating}
      confirmDisabled={!sourceProject}
      confirmIcon={<Copy className="h-4 w-4" />}
    >
      <Form {...form}>
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>New Project Title</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Separator />

        <div className="space-y-3">
             <h4 className="text-sm font-medium">Components to Copy</h4>
             {DUPLICATION_OPTION_DETAILS.map((item) => (
                <FormField
                    key={item.id}
                    control={form.control}
                    name={`options.${item.id}`}
                    render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                            <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={item.id === 'chapterContent' && !watchChapterOutlines}
                            />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                        <FormLabel>{item.label}</FormLabel>
                        <p className="text-xs text-muted-foreground">
                            {item.description}
                        </p>
                        </div>
                    </FormItem>
                    )}
                />
             ))}
        </div>

        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-800 flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5 shrink-0"/>
            <div>
                Resources such as uploaded images and text files are not copied.
            </div>
        </div>
      </Form>
    </BaseDialog>
  );
}
